import settings from './settings/settings.routes';
import conversation from './conversation/conversation.routes';
import { routes as searchRoutes } from '../../modules/search/search.routes';
import { routes as contactRoutes } from './contacts/routes';
import { routes as companyRoutes } from './companies/routes';
import { routes as notificationRoutes } from './notifications/routes';
import { routes as inboxRoutes } from './inbox/routes';
import { frontendURL } from '../../helper/URLHelper';
import helpcenterRoutes from './helpcenter/helpcenter.routes';
import campaignsRoutes from './campaigns/campaigns.routes';
import { routes as captainRoutes } from './captain/captain.routes';
import AppContainer from './Dashboard.vue';
import Suspended from './suspended/Index.vue';
import NoAccounts from './noAccounts/Index.vue';
import OnboardingAccountDetails from './onboarding/Index.vue';

// NooviChat: Appointments agenda
import appointmentsRoutes from './appointments/appointments.routes';
// NOOVI: Análise Comercial — feature nativa NooviChat (fase-31)
import commercialAnalysisRoutes from './commercialAnalysis/commercialAnalysis.routes';
// NooviChat: Broadcasts (Disparador em Massa)
import broadcastsRoutes from './broadcasts/broadcasts.routes';
// NooviChat: Chat Interno
import internalChatRoutes from './internal-chat/internalChat.routes';

// NooviChat: Pipeline and Follow Up imports
import PipelineView from './pipeline/PipelineView.vue';
import UnifiedPipelineDashboard from './pipeline/UnifiedPipelineDashboard.vue';
import store from 'dashboard/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import FollowUpsView from './follow-ups/FollowUpsView.vue';
import FollowUpTemplatesView from './follow-ups/FollowUpTemplatesView.vue';
import FollowUpAutomationsView from './follow-ups/FollowUpAutomationsView.vue';
import FollowUpTemplateBuilder from './follow-ups/FollowUpTemplateBuilder.vue';
import FlowBuilderView from './pipeline/FlowBuilderView.vue';
import SequencesIndex from './pipeline/sequences/SequencesIndex.vue';
import SequenceForm from './pipeline/sequences/SequenceForm.vue';

// Guard factory that redirects to dashboard when a feature flag is disabled.
// The sidebar already hides the menu, but this prevents direct URL access.
// Must be async to ensure account features are loaded before checking the flag
// (on page refresh, the accounts store is empty until AppContainer mounts).
const buildFeatureGuard = flag => async (to, from, next) => {
  const { accountId } = to.params;

  if (!store.getters['accounts/getAccount'](accountId)?.features) {
    await store.dispatch('accounts/get');
  }

  const isEnabled = store.getters['accounts/isFeatureEnabledonAccount'](
    accountId,
    flag
  );
  if (isEnabled) {
    next();
  } else {
    next({ path: `/app/accounts/${accountId}/dashboard` });
  }
};

const pipelineFeatureGuard = buildFeatureGuard(FEATURE_FLAGS.PIPELINE_BOARD);
// Audit 2026-05-07 — Sequences agora é flag independente, gerenciável no
// SuperAdmin sem afetar o resto do Pipeline Pro.
const pipelineSequencesFeatureGuard = buildFeatureGuard(
  FEATURE_FLAGS.PIPELINE_SEQUENCES
);

export default {
  routes: [
    {
      path: frontendURL('accounts/:accountId'),
      component: AppContainer,
      children: [
        // NooviChat: Unified Pipeline Dashboard (new unified view)
        {
          path: frontendURL('accounts/:accountId/pipeline/dashboard'),
          name: 'pipeline_dashboard_unified',
          component: UnifiedPipelineDashboard,
          meta: {
            permissions: [
              'administrator',
              'agent',
              'pipeline_manage',
              'pipeline_view',
            ],
            featureFlag: 'pipeline_board',
          },
          beforeEnter: pipelineFeatureGuard,
        },
        // NooviChat: Pipeline analytics - redirects to unified dashboard with pipeline=all
        {
          path: frontendURL('accounts/:accountId/pipeline/analytics'),
          name: 'pipeline_analytics',
          redirect: to => ({
            name: 'pipeline_dashboard_unified',
            params: { accountId: to.params.accountId },
            query: { pipeline: 'all' },
          }),
        },
        // NooviChat: Pipeline routes
        {
          path: frontendURL('accounts/:accountId/pipeline'),
          name: 'pipeline_index',
          component: PipelineView,
          meta: {
            permissions: [
              'administrator',
              'agent',
              'pipeline_manage',
              'pipeline_view',
            ],
            featureFlag: 'pipeline_board',
          },
          beforeEnter: pipelineFeatureGuard,
        },
        // Card-4.10 (2026-04-29) — admin de Sequence Definitions.
        // ATENÇÃO: declarado ANTES de `pipeline_with_funnel` porque
        // ambos casam /pipeline/<slug>; rotas mais específicas primeiro.
        // Audit 2026-05-07 — gating mudou de pipeline_board para
        // pipeline_sequences (flag independente, gerenciável no SuperAdmin).
        {
          path: frontendURL('accounts/:accountId/pipeline/sequences'),
          name: 'pipeline_sequences',
          component: SequencesIndex,
          meta: {
            permissions: ['administrator', 'agent', 'pipeline_manage'],
            featureFlag: 'pipeline_sequences',
          },
          beforeEnter: pipelineSequencesFeatureGuard,
        },
        {
          path: frontendURL('accounts/:accountId/pipeline/sequences/new'),
          name: 'pipeline_sequence_new',
          component: SequenceForm,
          meta: {
            permissions: ['administrator', 'agent', 'pipeline_manage'],
            featureFlag: 'pipeline_sequences',
          },
          beforeEnter: pipelineSequencesFeatureGuard,
        },
        {
          path: frontendURL('accounts/:accountId/pipeline/sequences/:id/edit'),
          name: 'pipeline_sequence_edit',
          component: SequenceForm,
          meta: {
            permissions: ['administrator', 'agent', 'pipeline_manage'],
            featureFlag: 'pipeline_sequences',
          },
          beforeEnter: pipelineSequencesFeatureGuard,
        },
        {
          path: frontendURL('accounts/:accountId/pipeline/:pipelineSlug'),
          name: 'pipeline_with_funnel',
          component: PipelineView,
          meta: {
            permissions: [
              'administrator',
              'agent',
              'pipeline_manage',
              'pipeline_view',
            ],
            featureFlag: 'pipeline_board',
          },
          beforeEnter: pipelineFeatureGuard,
        },
        {
          path: frontendURL('accounts/:accountId/pipeline/:pipelineSlug/:view'),
          name: 'pipeline_board',
          component: PipelineView,
          meta: {
            permissions: [
              'administrator',
              'agent',
              'pipeline_manage',
              'pipeline_view',
            ],
            featureFlag: 'pipeline_board',
          },
          beforeEnter: pipelineFeatureGuard,
        },
        {
          path: frontendURL(
            'accounts/:accountId/pipeline/:pipelineSlug/:view/:itemId'
          ),
          name: 'pipeline_item_detail',
          component: PipelineView,
          meta: {
            permissions: [
              'administrator',
              'agent',
              'pipeline_manage',
              'pipeline_view',
            ],
            featureFlag: 'pipeline_board',
          },
          beforeEnter: pipelineFeatureGuard,
        },
        // NooviChat: Follow Up routes
        {
          path: frontendURL('accounts/:accountId/follow-ups'),
          name: 'follow_ups',
          component: FollowUpsView,
          meta: {
            permissions: ['administrator', 'agent'],
            featureFlag: 'follow_ups',
          },
        },
        {
          path: frontendURL('accounts/:accountId/follow-ups/templates/new'),
          name: 'follow_up_template_new',
          component: FollowUpTemplateBuilder,
          meta: {
            permissions: ['administrator', 'follow_up_manage'],
            featureFlag: 'follow_ups',
          },
        },
        {
          path: frontendURL(
            'accounts/:accountId/follow-ups/templates/:templateId/edit'
          ),
          name: 'follow_up_template_edit',
          component: FollowUpTemplateBuilder,
          meta: {
            permissions: ['administrator', 'follow_up_manage'],
            featureFlag: 'follow_ups',
          },
        },
        {
          path: frontendURL('accounts/:accountId/follow-ups/templates'),
          name: 'follow_up_templates',
          component: FollowUpTemplatesView,
          meta: {
            permissions: ['administrator', 'follow_up_manage'],
            featureFlag: 'follow_ups',
          },
        },
        {
          path: frontendURL('accounts/:accountId/follow-ups/automations'),
          name: 'follow_up_automations',
          component: FollowUpAutomationsView,
          meta: {
            permissions: ['administrator', 'follow_up_manage'],
            featureFlag: 'follow_ups',
          },
        },
        // NooviChat: Flow Builder (visual automation builder)
        {
          path: frontendURL('accounts/:accountId/flow-builder'),
          name: 'flow_builder_index',
          component: FlowBuilderView,
          meta: {
            permissions: ['administrator'],
            featureFlag: 'flow_builder',
          },
        },
        ...captainRoutes,
        ...inboxRoutes,
        ...conversation.routes,
        ...settings.routes,
        ...contactRoutes,
        ...companyRoutes,
        ...searchRoutes,
        ...notificationRoutes,
        ...helpcenterRoutes.routes,
        ...campaignsRoutes.routes,
        // NooviChat: Appointments agenda (F2)
        ...appointmentsRoutes.routes,
        // NOOVI: Análise Comercial — feature nativa NooviChat (fase-31)
        ...commercialAnalysisRoutes.routes,
        // NooviChat: Broadcasts (Disparador em Massa)
        ...broadcastsRoutes.routes,
        // NooviChat: Chat Interno
        ...internalChatRoutes.routes,
      ],
    },
    {
      path: frontendURL('accounts/:accountId/onboarding'),
      name: 'onboarding_account_details',
      meta: {
        permissions: ['administrator', 'agent', 'custom_role'],
      },
      component: OnboardingAccountDetails,
    },
    {
      path: frontendURL('accounts/:accountId/suspended'),
      name: 'account_suspended',
      meta: {
        permissions: ['administrator', 'agent', 'custom_role'],
      },
      component: Suspended,
    },
    {
      path: frontendURL('no-accounts'),
      name: 'no_accounts',
      component: NoAccounts,
    },
  ],
};
